/**
 * HSIA bra size calculator
 *
 * The current HSIA size chart is the single source of truth for these rules.
 * Measurements are in inches. Half-inch under-bust values round down and
 * half-inch over-bust values round up before the chart is consulted.
 *
 * Markup contract (each calculator is scoped to its own root):
 *   [data-hsia-bra-calculator]
 *     [data-hsia-under-bust]
 *     [data-hsia-over-bust]
 *     [data-hsia-size-result]
 *     [data-hsia-sister-result-container] (optional)
 *       [data-hsia-sister-size-result] (optional)
 *     [data-hsia-calculator-message] (optional)
 */
(function (globalScope, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!globalScope || !globalScope.document) {
    return;
  }

  // Shopify can render the same section asset more than once. Reuse an
  // existing copy so roots never receive duplicate listeners.
  if (
    globalScope.HsiaBraSizeCalculator &&
    globalScope.HsiaBraSizeCalculator.version === api.version
  ) {
    globalScope.HsiaBraSizeCalculator.initAll(globalScope.document);
    return;
  }

  globalScope.HsiaBraSizeCalculator = api;
  api.boot(globalScope.document);
})(typeof window !== 'undefined' ? window : undefined, function () {
  'use strict';

  var VERSION = '1.1.0';
  var NOT_AVAILABLE = 'Not available';
  var HALF_INCH_TOLERANCE = 1e-9;

  var CUP_SIZES = Object.freeze([
    'A',
    'B',
    'C',
    'D',
    'DD/E',
    'DDD/F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L'
  ]);

  // Each rule represents one column in the approved size chart.
  var SIZE_RULES = Object.freeze([
    Object.freeze({ underMin: 24, underMax: 25, band: 28, overStart: 28 }),
    Object.freeze({ underMin: 26, underMax: 27, band: 30, overStart: 30 }),
    Object.freeze({ underMin: 28, underMax: 29, band: 32, overStart: 32 }),
    Object.freeze({ underMin: 30, underMax: 31, band: 34, overStart: 34 }),
    Object.freeze({ underMin: 32, underMax: 33, band: 36, overStart: 37 }),
    Object.freeze({ underMin: 34, underMax: 37, band: 38, overStart: 39 }),
    Object.freeze({ underMin: 38, underMax: 40, band: 40, overStart: 41 }),
    Object.freeze({ underMin: 41, underMax: 43, band: 42, overStart: 43 }),
    Object.freeze({ underMin: 44, underMax: 45, band: 44, overStart: 47 }),
    Object.freeze({ underMin: 46, underMax: 48, band: 46, overStart: 49 }),
    Object.freeze({ underMin: 49, underMax: 51, band: 48, overStart: 51 })
  ]);

  // Preserve the published sister-size chart exactly. The calculator uses
  // DD/E and DDD/F, while the legacy sister chart labels those cups E and F.
  var SISTER_SIZE_GROUPS = Object.freeze([
    Object.freeze(['30B', '32A', '34AA']),
    Object.freeze(['30C', '32B', '34A']),
    Object.freeze(['30D', '32C', '34B', '36A']),
    Object.freeze(['30E', '32D', '34C', '36B', '38A']),
    Object.freeze(['30F', '32E', '34D', '36C', '38B', '40A']),
    Object.freeze(['30G', '32F', '34E', '36D', '38C', '40B', '42A']),
    Object.freeze(['32G', '34F', '36E', '38D', '40C', '42B', '44A']),
    Object.freeze(['34G', '36F', '38E', '40D', '42C', '44B', '46A']),
    Object.freeze(['36G', '38F', '40E', '42D', '44C', '46B']),
    Object.freeze(['38G', '40F', '42E', '44D', '46C']),
    Object.freeze(['40G', '42F', '44E', '46D']),
    Object.freeze(['42G', '44F', '46E']),
    Object.freeze(['44G', '46F']),
    Object.freeze(['46G'])
  ]);

  var SELECTORS = Object.freeze({
    root: '[data-hsia-bra-calculator]',
    underBust: '[data-hsia-under-bust]',
    overBust: '[data-hsia-over-bust]',
    result: '[data-hsia-size-result]',
    sisterResultContainer: '[data-hsia-sister-result-container]',
    sisterResult: '[data-hsia-sister-size-result]',
    message: '[data-hsia-calculator-message]'
  });

  var instances = new WeakMap();
  var bootedDocuments = new WeakSet();

  function parseMeasurement(value, minimum, maximum) {
    if (value === null || typeof value === 'undefined') {
      return null;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }

    var numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return null;
    }

    if (numericValue < minimum || numericValue > maximum) {
      return null;
    }

    // The published UI works in whole- and half-inch increments only.
    if (
      Math.abs(numericValue * 2 - Math.round(numericValue * 2)) >
      HALF_INCH_TOLERANCE
    ) {
      return null;
    }

    return numericValue;
  }

  function findRule(normalizedUnderBust) {
    for (var index = 0; index < SIZE_RULES.length; index += 1) {
      var rule = SIZE_RULES[index];

      if (
        normalizedUnderBust >= rule.underMin &&
        normalizedUnderBust <= rule.underMax
      ) {
        return rule;
      }
    }

    return null;
  }

  /**
   * Returns calculation details, or null when the measurements do not map to
   * a coloured cell in the current size chart.
   */
  function getSizeDetails(underBustValue, overBustValue) {
    var underBust = parseMeasurement(underBustValue, 24, 51);
    var overBust = parseMeasurement(overBustValue, 28, 62);

    if (underBust === null || overBust === null) {
      return null;
    }

    var normalizedUnderBust = Math.floor(underBust);
    var normalizedOverBust = Math.ceil(overBust);
    var rule = findRule(normalizedUnderBust);

    if (!rule) {
      return null;
    }

    var cupIndex = normalizedOverBust - rule.overStart;

    if (cupIndex < 0 || cupIndex >= CUP_SIZES.length) {
      return null;
    }

    return {
      size: String(rule.band) + CUP_SIZES[cupIndex],
      band: rule.band,
      cup: CUP_SIZES[cupIndex],
      normalizedUnderBust: normalizedUnderBust,
      normalizedOverBust: normalizedOverBust
    };
  }

  function calculateSize(underBustValue, overBustValue) {
    var details = getSizeDetails(underBustValue, overBustValue);
    return details ? details.size : NOT_AVAILABLE;
  }

  function normalizeSisterLookupSize(sizeValue) {
    if (typeof sizeValue !== 'string' || sizeValue.trim() === '') {
      return null;
    }

    var match = sizeValue
      .trim()
      .toUpperCase()
      .match(/^(\d{2})(AA|DDD\/F|DD\/E|[A-L])$/);

    if (!match) {
      return null;
    }

    var cup = match[2];

    if (cup === 'DD/E') {
      cup = 'E';
    } else if (cup === 'DDD/F') {
      cup = 'F';
    }

    return match[1] + cup;
  }

  /**
   * Returns the other sizes from the matching published sister-size row.
   * No relationship is inferred when the size does not appear in that chart.
   */
  function getSisterSizes(sizeValue) {
    var lookupSize = normalizeSisterLookupSize(sizeValue);

    if (!lookupSize) {
      return [];
    }

    for (var groupIndex = 0; groupIndex < SISTER_SIZE_GROUPS.length; groupIndex += 1) {
      var group = SISTER_SIZE_GROUPS[groupIndex];

      if (group.indexOf(lookupSize) === -1) {
        continue;
      }

      var sisterSizes = [];

      for (var sizeIndex = 0; sizeIndex < group.length; sizeIndex += 1) {
        if (group[sizeIndex] !== lookupSize) {
          sisterSizes.push(group[sizeIndex]);
        }
      }

      return sisterSizes;
    }

    return [];
  }

  function isEmptyMeasurement(value) {
    return value === null ||
      typeof value === 'undefined' ||
      (typeof value === 'string' && value.trim() === '');
  }

  function formatMeasurement(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  /**
   * Rebuilds a measurement select only when its options differ from the
   * approved range. Its first (placeholder) option is preserved verbatim.
   */
  function populateMeasurementOptions(selectElement, minimum, maximum) {
    if (
      !selectElement ||
      String(selectElement.tagName).toUpperCase() !== 'SELECT' ||
      !selectElement.options ||
      !selectElement.ownerDocument ||
      typeof selectElement.ownerDocument.createElement !== 'function'
    ) {
      return false;
    }

    var desiredValues = [];

    for (var doubled = minimum * 2; doubled <= maximum * 2; doubled += 1) {
      desiredValues.push(formatMeasurement(doubled / 2));
    }

    var optionsAlreadyMatch =
      selectElement.options.length === desiredValues.length + 1;

    if (optionsAlreadyMatch) {
      for (var optionIndex = 0; optionIndex < desiredValues.length; optionIndex += 1) {
        if (selectElement.options[optionIndex + 1].value !== desiredValues[optionIndex]) {
          optionsAlreadyMatch = false;
          break;
        }
      }
    }

    if (optionsAlreadyMatch) {
      return false;
    }

    var previousValue = selectElement.value;

    while (selectElement.options.length > 1) {
      selectElement.remove(1);
    }

    for (var valueIndex = 0; valueIndex < desiredValues.length; valueIndex += 1) {
      var value = desiredValues[valueIndex];
      var option = selectElement.ownerDocument.createElement('option');
      option.value = value;
      option.textContent = value;
      selectElement.appendChild(option);
    }

    selectElement.value = desiredValues.indexOf(previousValue) >= 0
      ? previousValue
      : '';

    return true;
  }

  function unavailableLabel(root) {
    return root.getAttribute('data-hsia-unavailable-label') || NOT_AVAILABLE;
  }

  function renderMessage(root, messageElement, details, underValue, overValue) {
    if (!messageElement) {
      return;
    }

    var underBust = parseMeasurement(underValue, 24, 51);
    var overBust = parseMeasurement(overValue, 28, 62);

    if (details) {
      messageElement.textContent = '';
      return;
    }

    if (isEmptyMeasurement(underValue) || isEmptyMeasurement(overValue)) {
      messageElement.textContent =
        root.getAttribute('data-hsia-empty-message') ||
        'Select both measurements to see your recommended HSIA size.';
      return;
    }

    if (underBust === null || overBust === null) {
      messageElement.textContent =
        root.getAttribute('data-hsia-invalid-message') ||
        'Select measurements in 0.5-inch increments within the supported range.';
      return;
    }

    messageElement.textContent =
      root.getAttribute('data-hsia-no-match-message') ||
      'This measurement combination is not available in the current size chart.';
  }

  function bind(root) {
    if (!root || typeof root.querySelector !== 'function') {
      return null;
    }

    if (instances.has(root)) {
      return instances.get(root);
    }

    var underBustElement = root.querySelector(SELECTORS.underBust);
    var overBustElement = root.querySelector(SELECTORS.overBust);
    var resultElement = root.querySelector(SELECTORS.result);
    var sisterResultContainerElement = root.querySelector(
      SELECTORS.sisterResultContainer
    );
    var sisterResultElement = root.querySelector(SELECTORS.sisterResult);
    var messageElement = root.querySelector(SELECTORS.message);

    // An incomplete block is ignored safely. This keeps Theme Editor previews
    // and partially-rendered sections free from runtime errors.
    if (!underBustElement || !overBustElement || !resultElement) {
      return null;
    }

    populateMeasurementOptions(underBustElement, 24, 51);
    populateMeasurementOptions(overBustElement, 28, 62);

    function render() {
      var isWaiting =
        isEmptyMeasurement(underBustElement.value) ||
        isEmptyMeasurement(overBustElement.value);
      var details = getSizeDetails(
        underBustElement.value,
        overBustElement.value
      );
      var sisterSizes = details ? getSisterSizes(details.size) : [];

      resultElement.textContent = isWaiting
        ? root.getAttribute('data-hsia-empty-label') || '—'
        : details
          ? details.size
          : unavailableLabel(root);
      resultElement.setAttribute(
        'data-hsia-size-status',
        isWaiting ? 'waiting' : details ? 'available' : 'not-available'
      );
      root.setAttribute(
        'data-hsia-calculator-state',
        isWaiting ? 'waiting' : details ? 'available' : 'not-available'
      );

      if (sisterResultElement) {
        sisterResultElement.textContent = details
          ? sisterSizes.length
            ? sisterSizes.join(' · ')
            : root.getAttribute('data-hsia-sister-no-match-label') ||
              'No sister sizes available for this result.'
          : root.getAttribute('data-hsia-empty-label') || '—';
        sisterResultElement.setAttribute(
          'data-hsia-sister-status',
          !details
            ? isWaiting ? 'waiting' : 'not-available'
            : sisterSizes.length ? 'available' : 'not-available'
        );
      }

      if (sisterResultContainerElement) {
        sisterResultContainerElement.hidden = !details;
      }

      renderMessage(
        root,
        messageElement,
        details,
        underBustElement.value,
        overBustElement.value
      );

      return details;
    }

    underBustElement.addEventListener('input', render);
    underBustElement.addEventListener('change', render);
    overBustElement.addEventListener('input', render);
    overBustElement.addEventListener('change', render);

    var instance = {
      render: render,
      destroy: function () {
        underBustElement.removeEventListener('input', render);
        underBustElement.removeEventListener('change', render);
        overBustElement.removeEventListener('input', render);
        overBustElement.removeEventListener('change', render);
        instances.delete(root);
      }
    };

    instances.set(root, instance);
    render();

    return instance;
  }

  function initAll(scope) {
    if (!scope) {
      return [];
    }

    var roots = [];

    if (typeof scope.matches === 'function' && scope.matches(SELECTORS.root)) {
      roots.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      var descendants = scope.querySelectorAll(SELECTORS.root);

      for (var index = 0; index < descendants.length; index += 1) {
        roots.push(descendants[index]);
      }
    }

    var initialized = [];

    for (var rootIndex = 0; rootIndex < roots.length; rootIndex += 1) {
      var instance = bind(roots[rootIndex]);

      if (instance) {
        initialized.push(instance);
      }
    }

    return initialized;
  }

  function boot(documentObject) {
    if (!documentObject || bootedDocuments.has(documentObject)) {
      return;
    }

    bootedDocuments.add(documentObject);

    if (documentObject.readyState === 'loading') {
      documentObject.addEventListener(
        'DOMContentLoaded',
        function () {
          initAll(documentObject);
        },
        { once: true }
      );
    } else {
      initAll(documentObject);
    }

    // Re-initialize calculator blocks added dynamically by the Theme Editor.
    documentObject.addEventListener('shopify:section:load', function (event) {
      initAll(event.target || documentObject);
    });
  }

  return Object.freeze({
    version: VERSION,
    NOT_AVAILABLE: NOT_AVAILABLE,
    CUP_SIZES: CUP_SIZES,
    SIZE_RULES: SIZE_RULES,
    SISTER_SIZE_GROUPS: SISTER_SIZE_GROUPS,
    calculateSize: calculateSize,
    getSizeDetails: getSizeDetails,
    getSisterSizes: getSisterSizes,
    populateMeasurementOptions: populateMeasurementOptions,
    bind: bind,
    initAll: initAll,
    boot: boot
  });
});
